import {
  companyDashboardApi,
  DataDashboardResult,
  ICompanyDashboardResultVo,
  PlatformSummary,
} from '@/axios/company';
import OperatorBtns from '@/components/Table/operateBtns';
import { useRequest, useSetState } from 'ahooks';
import { Button, Checkbox, Flex } from 'antd';
import { openCreateArticleModal } from '../Article';
import { ColumnsType, TableProps } from 'antd/es/table';
import { useState } from 'react';
import CommonModalFactory from '@/plugins/ModalFactory';
import { postRunRecordDetail } from '@/axios/geo';
import { useStore } from '@/stores';
import { IPlatformItem } from '@/axios/common';
import EllipsisText from '@/components/Table/EllipsisText';
import CommonTable from '@/components/Table';

type DataDashboardRow = Omit<DataDashboardResult, 'children'> & {
  children?: PlatformSummary[];
  invisible?: number;
};

const DataDashboard = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const companyId = searchParams.get('companyId');
  const [dataSource, setDataSource] = useState<DataDashboardRow[]>([]);
  const { data: summaryData, loading } = useRequest(() => {
    return companyDashboardApi.getSummary(companyId!);
  }, {
    onSuccess: (data) => {
      setDataSource(data.result || []);
    }
  });
  const appStore = useStore('app');
  const columns: ColumnsType<DataDashboardRow> = [
    {
      title: '核心词',
      dataIndex: 'coreWord',
      render: (_: any, record: any) => {
        return record.coreWord || '未知';
      },
    },
    {
      title: '平台',
      dataIndex: 'platform',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
        return (
          <div style={{ padding: 8 }}>
            <Flex vertical>
              {appStore.platformList?.map((item) => (
                <div key={item.platform}>
                  <Checkbox
                    checked={selectedKeys.includes(item.platform)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedKeys([...selectedKeys, item.platform]);
                      } else {
                        setSelectedKeys(selectedKeys.filter((key) => key !== item.platform));
                      }
                    }}
                  >
                    {item.name}
                  </Checkbox>
                </div>
              ))}
            </Flex>
            <Flex gap={8} style={{ marginTop: 8 }}>
              <Button
                type="primary"
                onClick={() => {
                  if (!selectedKeys.length) {
                    setDataSource(summaryData?.result || []);
                    confirm();
                    return;
                  }
                  const newDataSource: DataDashboardRow[] = [];
                  for (const item of summaryData?.result || []) {
                    const selectedPlatforms = selectedKeys.map((key) => String(key));
                    const matchedChildren = item.children.filter((child: PlatformSummary) =>
                      selectedPlatforms.includes(child.platform)
                    );
                    matchedChildren.forEach((child) => {
                      newDataSource.push({
                        coreWord: item.coreWord,
                        platform: child.platform,
                        visibility: child.visibility,
                        invisible: (child as any).invisible,
                        total: child.total,
                      });
                    });
                  }
                  setDataSource(newDataSource);
                  confirm();
                }}
                size="small"
              >
                确定
              </Button>
              <Button
                onClick={() => {
                  setDataSource(summaryData?.result || []);
                  clearFilters && clearFilters();
                  confirm();
                }}
                size="small"
              >
                重置
              </Button>
            </Flex>
          </div>
        );
      },
      render: (_: any, record: any) => {
        const platformName = record.platform
          ? appStore.platformList.find((item) => item.platform === record.platform)?.name || record.platform
          : '全部平台';
        return platformName;
      },
    },
    {
      title: '可见问题数',
      dataIndex: 'visibility',
    },
    {
      title: '不可见问题数',
      dataIndex: 'invisible',
      render: (_: any, record: any) => {
        return record.invisible ?? 0;
      },
    },
    {
      title: '曝光度',
      dataIndex: 'exposure',
      render: (_: any, record: any) => {
        const visibility = record.visibility || 0;
        const total = record.total || 1;
        return parseFloat(((visibility / total) * 100).toFixed(2)) + '%';
      },
    },
    {
      title: '操作',
      dataIndex: 'operate',
      render: (_: any, record: any) => {
        return (
          <OperatorBtns
            btns={[
              {
                type: 'common',
                text: '生成文章',
                onClick: () => {
                  console.log('生成文章', record);
                  openCreateArticleModal({
                    companyId: companyId!,
                    selectedCoreWords: [record.coreWord],
                  });
                },
                permission: ['company:article:create'],
              },
              {
                type: 'common',
                text: '查看结果',
                onClick: () => {
                  const platformName = record.platform
                    ? appStore.platformList.find((item) => item.platform === record.platform)?.name || record.platform
                    : '全部平台';
                  openResultModal(
                    {
                      taskId: summaryData?.taskId,
                      coreWord: record.coreWord,
                      platform: record.platform || undefined,
                      platformName,
                      platformList: appStore.platformList,
                    },
                    {
                      title: `${record.coreWord} (${platformName})`,
                    }
                  );
                },
              },
            ]}
          />
        );
      },
    },
  ];
  return (
    <div>
      <Flex gap={8} align="center" style={{ marginBottom: 16 }}>
        <div>曝光度：{(((summaryData?.visibility || 0) / (summaryData?.total || 1)) * 100).toFixed(2)}%</div>
        <div>更新时间：{summaryData?.updateTime ? new Date(summaryData?.updateTime).toLocaleString() : '未知'}</div>
      </Flex>
      <div>
        <CommonTable<DataDashboardRow>
          rowKey={(record) => record.coreWord + record.platform}
          loading={loading}
          columns={columns}
          dataSource={dataSource}
          pagination={false}
        />
      </div>
    </div>
  );
};
export default DataDashboard;

const openResultModal = CommonModalFactory<{
  result: ICompanyDashboardResultVo;
  platformName?: string;
  platformList?: IPlatformItem[];
}>(
  {
    title: '查看结果',
    width: 1000,
  },
  ({ data }) => {
    const { result, platformList } = data;
    const columns = [
      {
        title: '问题',
        dataIndex: 'word',
        ellipsis: true,
        render: (val: string) => <EllipsisText text={val} />,
      },
      {
        title: '平台',
        dataIndex: 'platform',
        render: (_: any, record: any) => {
          return platformList?.find((item) => item.platform === record.platform)?.name || '全部平台';
        },
      },
    ];
    return (
      <div>
        <Flex gap={8}>
          <Flex vertical gap={20} style={{ flex: 1, minWidth: 0 }}>
            <div>可见问题数（{result.visibilities.length}）</div>
            <CommonTable<any>
              rowKey="word"
              columns={columns}
              dataSource={result.visibilities}
              pagination={false}
              scroll={{ y: 400 }}
            />
          </Flex>
          <Flex vertical gap={20} style={{ flex: 1, minWidth: 0 }}>
            <div>不可见问题数（{result.invisibles.length}）</div>
            <CommonTable<any>
              rowKey="word"
              columns={columns}
              dataSource={result.invisibles}
              pagination={false}
              scroll={{ y: 400 }}
            />
          </Flex>
        </Flex>
      </div>
    );
  },
  async ({ taskId, coreWord, platform, ...props }) => {
    const list = await companyDashboardApi.getDetail({
      taskId,
      coreWord,
      platform,
    });
    return {
      ...props,
      result: list,
    };
  }
);
